import React from 'react';
import { Check, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { useContent } from '../hooks/useContent';

export const HeroSection = () => {
  const { content, loading } = useContent();
  
  if (loading || !content) {
    return (
      <section className="relative min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 overflow-hidden flex items-center justify-center">
        <div className="text-navy-900 text-xl">Loading...</div>
      </section>
    );
  }

  const { hero, siteInfo } = content;

  const handleBookConsultation = () => {
    // Open Google Calendar in new tab
    window.open(siteInfo.calendarLink, '_blank');
    
    // Redirect current tab to thanks page
    setTimeout(() => {
      window.location.href = '/thanks';
    }, 100);
  };

  return (
    <section className="relative min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 overflow-hidden">
      {/* Geometric Background Elements - Similar to reference */}
      <div className="absolute inset-0">
        {/* Large geometric shapes */}
        <div className="absolute top-20 right-0 w-96 h-96 bg-gradient-to-br from-blue-600/10 to-navy-900/20 transform rotate-12 rounded-3xl"></div>
        <div className="absolute top-40 right-20 w-64 h-64 bg-gradient-to-br from-navy-900/15 to-blue-800/10 transform -rotate-6 rounded-2xl"></div>
        <div className="absolute bottom-20 left-0 w-80 h-80 bg-gradient-to-tr from-blue-500/8 to-navy-800/12 transform rotate-45 rounded-3xl"></div>
        
        {/* Smaller accent shapes */}
        <div className="absolute top-1/3 left-1/4 w-32 h-32 bg-blue-600/5 transform rotate-45 rounded-xl"></div>
        <div className="absolute bottom-1/3 right-1/3 w-24 h-24 bg-navy-900/8 transform -rotate-12 rounded-lg"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center min-h-screen py-20">
          
          {/* Left Content */}
          <div className="space-y-8">
            <div className="space-y-6">
              <h1 className="text-5xl lg:text-6xl font-bold text-navy-900 leading-tight">
                "{hero.mainHeading}<br />
                <span className="text-blue-600">{hero.subHeading}</span>"
              </h1>
              
              <p className="text-xl lg:text-2xl text-gray-700 leading-relaxed">
                {hero.description}
              </p>
            </div>

            {/* Hero Bullets */}
            <div className="space-y-4">
              {hero.bullets.map((bullet, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <div className="flex-shrink-0 mt-1">
                    <Check className="h-5 w-5 text-green-600" />
                  </div>
                  <p className="text-gray-700">{bullet}</p>
                </div>
              ))}
            </div>

            {/* CTA Button */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg"
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg font-semibold transition-all duration-200 hover:transform hover:scale-105"
                onClick={handleBookConsultation}
              >
                {hero.ctaText}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>

          {/* Right Image */}
          <div className="relative w-full">
            <div className="relative z-10 rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="https://images.unsplash.com/photo-1669101283985-ab2b293923df?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxwaGFybWFjZXV0aWNhbCUyMGxhYm9yYXRvcnl8ZW58MHx8fHwxNzU5OTI4MDUxfDA&ixlib=rb-4.1.0&q=85"
                alt={hero.imageAlt}
                className="w-full h-[500px] object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
              
              {/* Fallback background image */}
              <div 
                className="w-full h-[500px] bg-cover bg-center bg-no-repeat"
                style={{
                  backgroundImage: `url('https://images.unsplash.com/photo-1669101283985-ab2b293923df?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxwaGFybWFjZXV0aWNhbCUyMGxhYm9yYXRvcnl8ZW58MHx8fHwxNzU5OTI4MDUxfDA&ixlib=rb-4.1.0&q=85&w=600&h=500')`,
                  display: 'none'
                }}
              >
                {/* Overlay gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-navy-900/20 to-transparent"></div>
              </div>
            </div>
            
            {/* Floating stats card */}
            <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl border z-20">
              <div className="text-3xl font-bold text-navy-900">100%</div>
              <div className="text-sm text-gray-600">Audit-Ready Projects</div>
            </div>
            
            {/* Floating experience card */}
            <div className="absolute -top-6 -right-6 bg-white p-6 rounded-xl shadow-xl border z-20">
              <div className="text-3xl font-bold text-blue-600">36+</div>
              <div className="text-sm text-gray-600">Years Experience</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};